import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { evaluate } from 'mathjs';
import { API_CONFIG } from '../../../utils/apiConfig';
import './SideBar.css';

const AccountsSidebar = ({ user = {}, isSidebarOpen, setIsSidebarOpen, setShowProfile, handleLogout, staffList = [] }) => {
  const navigate = useNavigate();
  const [showCalculator, setShowCalculator] = useState(false);
  const [showMessageBox, setShowMessageBox] = useState(false);
  const [expression, setExpression] = useState('');
  const [result, setResult] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(() => {
    const savedPhoto = localStorage.getItem('accountsAdminProfilePhoto');
    return savedPhoto || user.profilePhoto || null;
  });
  const [messageText, setMessageText] = useState('');
  const [selectedStaffForMessage, setSelectedStaffForMessage] = useState([]);
  const [messageStaffList, setMessageStaffList] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const sidebarRef = useRef(null);

  const calculate = () => {
    try {
      const res = evaluate(expression);
      setResult(res);
    } catch (error) {
      setResult('Error');
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const photoUrl = event.target.result;
        setProfilePhoto(photoUrl);
        localStorage.setItem('accountsAdminProfilePhoto', photoUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhoto(null);
    localStorage.removeItem('accountsAdminProfilePhoto');
  };

  const scrollSidebar = (direction) => {
    if (sidebarRef.current) {
      const scrollAmount = 100;
      const currentScroll = sidebarRef.current.scrollTop;
      sidebarRef.current.scrollTo({
        top: direction === 'up' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const fetchStaffForMessage = async () => {
    setIsLoadingStaff(true);
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/accounts/staff/list`, {
        headers: {
          'Authorization': 'Bearer mock-token-for-testing',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessageStaffList(data || []);
      } else {
        setMessageStaffList([
          { _id: '1', name: 'John Doe', role: 'staff' },
          { _id: '2', name: 'Jane Smith', role: 'staff' },
          { _id: '3', name: 'Mike Johnson', role: 'staff' }
        ]);
      }
    } catch (error) {
      setMessageStaffList([
        { _id: '1', name: 'John Doe', role: 'staff' },
        { _id: '2', name: 'Jane Smith', role: 'staff' },
        { _id: '3', name: 'Mike Johnson', role: 'staff' }
      ]);
    } finally {
      setIsLoadingStaff(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleBillingDetails = () => {
    handleNavigation('/billing-details');
  };

  const handleBillDistribution = () => {
    handleNavigation('/bill-distribution');
  };

  const handlePaymentHistory = () => {
    handleNavigation('/payment-history');
  };


  const handleBillSummary = () => {
    handleNavigation('/billing-details');
  };

  const handleStaffList = () => {
    handleNavigation('/staff-list');
  };

  const handleSendMessage = async () => {
    if (!selectedStaffForMessage || selectedStaffForMessage.length === 0 || !messageText.trim()) {
      alert('Please select at least one staff member and enter a message');
      return;
    }

    try {
      const token = localStorage.getItem('token') || 'mock-token-for-testing';
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const senderName = currentUser.name || 'Accounts Admin';
      const senderEmail = currentUser.email || 'accounts@admin.com';

      const messagePromises = selectedStaffForMessage.map(async (staffId) => {
        const messageData = {
          staffId: staffId,
          message: messageText,
          sender: senderName,
          senderEmail: senderEmail,
          senderRole: 'Accounts Admin',
          type: 'notification',
          isRead: false,
          createdAt: new Date().toISOString(),
          priority: 'normal'
        };

        const response = await fetch(`${API_CONFIG.BASE_URL}/api/marketing/send-message`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(messageData)
        });

        if (!response.ok) {
          throw new Error(`Failed to send message to staff ${staffId}`);
        }

        return response.json();
      });

      await Promise.all(messagePromises);
      
      alert(`Message sent successfully to ${selectedStaffForMessage.length} staff member(s)!`);
      setSelectedStaffForMessage([]);
      setMessageText('');
      setShowMessageBox(false);
    } catch (err) {
      alert(`Failed to send message: ${err.message}`);
    }
  };

  return (
    <>
      <div className={`accounts-sidebar-wrapper ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="accounts-sidebar" ref={sidebarRef}>
          <div className="scroll-buttons">
            <button
              className="scroll-btn scroll-up"
              onClick={() => scrollSidebar('up')}
              aria-label="Scroll sidebar up"
            >
              ↑
            </button>
            <button
              className="scroll-btn scroll-down"
              onClick={() => scrollSidebar('down')}
              aria-label="Scroll sidebar down"
            >
              ↓
            </button>
          </div>
          <div className="accounts-profile-section">
            <div
              className="accounts-profile-pic"
              style={{ backgroundImage: profilePhoto ? `url(${profilePhoto})` : 'none' }}
              onClick={() => document.getElementById('profile-photo-upload').click()}
              role="button"
              aria-label="Upload profile photo"
            >
              {!profilePhoto && <span>No Photo</span>}
            </div>
            <input
              type="file"
              id="profile-photo-upload"
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              aria-hidden="true"
            />
            {profilePhoto && (
              <button
                onClick={handleRemovePhoto}
                className="remove-photo-btn"
                aria-label="Remove profile photo"
                title="Remove profile photo"
              >
                ✕
              </button>
            )}
            <div className="accounts-profile-name">{user.name || 'Accounts Admin'}</div>
          </div>

          <div className="accounts-profile-summary">
            <p>Email:accounts@admin.com</p>
            <p>Role:Accounts Admin</p>
            <p>Department:'Accounts'</p>
            <p>Status: Active</p>
          </div>

          <div className="accounts-menu-label">Calculator</div>
          <div className="mini-calculator">
            <input
              type="text"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              placeholder="Enter calculation"
              className="calc-input"
            />
            <div className="calcculate-buttons">
              <button onClick={() => setExpression(expression + '7')}>7</button>
              <button onClick={() => setExpression(expression + '8')}>8</button>
              <button onClick={() => setExpression(expression + '9')}>9</button>
              <button onClick={() => setExpression(expression + '/')}>/</button>
              <button onClick={() => setExpression(expression + '4')}>4</button>
              <button onClick={() => setExpression(expression + '5')}>5</button>
              <button onClick={() => setExpression(expression + '6')}>6</button>
              <button onClick={() => setExpression(expression + '*')}>*</button>
              <button onClick={() => setExpression(expression + '1')}>1</button>
              <button onClick={() => setExpression(expression + '2')}>2</button>
              <button onClick={() => setExpression(expression + '3')}>3</button>
              <button onClick={() => setExpression(expression + '-')}>-</button>
              <button onClick={() => setExpression(expression + '0')}>0</button>
              <button onClick={() => setExpression(expression + '.')}>.</button>
              <button onClick={calculate}>=</button>
              <button onClick={() => setExpression(expression + '+')}>+</button>
              <button onClick={() => setExpression('')} className="clear-btn">C</button>
            </div>
            {result && <div className="calc-result">= {result}</div>}
          </div>

          <div className="accounts-menu-label">Billing</div>
          <ul>
            <li
              onClick={handleBillingDetails}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleBillingDetails()}
              aria-label="Navigate to Billing Details"
            >
              Billing Details
            </li>
            <li
              onClick={handleBillDistribution}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleBillDistribution()}
              aria-label="Navigate to Bill Distribution"
            >
              Bill Distribution
            </li>
            <li
              onClick={handlePaymentHistory}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handlePaymentHistory()}
              aria-label="Navigate to Payment History"
            >
              Payment History
            </li>
            <li
              onClick={handleBillSummary}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleBillSummary()}
              aria-label="Navigate to Bill Summary"
            >
              Bill Summary
            </li>
    
          </ul>

          <div className="accounts-menu-label">Management</div>
          <ul>
            <li
              onClick={handleStaffList}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleStaffList()}
              aria-label="Navigate to Staff List"
            >
              Staff List
            </li>
          </ul>

          <div className="accounts-menu-label">Tools</div>
          <ul>
            <li
              onClick={() => setShowCalculator(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setShowCalculator(true)}
              aria-label="Open Mini Calculator"
            >
              Mini Calculator
            </li>
          </ul>

          <div className="accounts-menu-label">Notifications</div>
          <ul>
            <li
              onClick={() => {
                setShowMessageBox(true);
                fetchStaffForMessage();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setShowMessageBox(true)}
              aria-label="Send Message to Staff"
            >
              Send Message to Staff
            </li>
          </ul>
          <ul>
      
            <li
            className='logoutbtn'
              onClick={handleLogout}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleLogout()}
              aria-label="Log out"
            >
              Logout
            </li>
          </ul>
        </div>
      </div>

      {showCalculator && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Mini Calculator</h3>
            <input
              type="text"
              placeholder="e.g., 500 + 100 * 2"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              style={{ width: '100%', padding: '10px', fontSize: '16px' }}
              aria-label="Calculator input"
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={calculate}
                className="add-feature-btn"
                style={{ flex: 1 }}
                aria-label="Calculate"
              >
                Calculate
              </button>
              <button
                onClick={() => setShowCalculator(false)}
                style={{ 
                  flex: 1, 
                  padding: '10px', 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                aria-label="Close calculator"
              >
                Cancel
              </button>
            </div>
            {result !== '' && (
              <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
                Result: {result}
              </p>
            )}
          </div>
        </div>
      )}

      {showMessageBox && (
        <div className="modal-overlay">
          <div className="modal-content message-box-modal">
            <h3>Send Message to Staff</h3>
            
            <div className="message-box-section">
              <label>Select Staff Members:</label>
              {isLoadingStaff ? (
                <div className="loading-staff">Loading staff...</div>
              ) : (
                <div className="staff-selection">
                  {messageStaffList.map((staff) => (
                    <label key={staff._id} className="staff-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedStaffForMessage.includes(staff._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStaffForMessage([...selectedStaffForMessage, staff._id]);
                          } else {
                            setSelectedStaffForMessage(selectedStaffForMessage.filter(id => id !== staff._id));
                          }
                        }}
                      />
                      {staff.name} ({staff.role})
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="message-box-section">
              <label>Message:</label>
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter your message here..."
                rows="4"
                className="message-textarea"
              />
            </div>

            <div className="message-box-actions">
              <button
                onClick={handleSendMessage}
                className="send-message-btn"
                disabled={!messageText.trim() || selectedStaffForMessage.length === 0}
              >
                Send Message
              </button>
              <button
                onClick={() => setShowMessageBox(false)}
                className="cancel-message-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountsSidebar;